import { useCallback, useEffect, useMemo } from 'react';
import { useDebounce } from 'use-debounce';

import {
  type WorkspaceAgentMentionRowItem,
  type WorkspaceAgentMentionSearchResult,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { WORKSPACE_AGENT_MENTION_SEARCH_DEBOUNCE_MS } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionConstants';
import { getWorkspaceAgentMentionMatchStrength } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';

const EMPTY_ITEMS: WorkspaceAgentMentionRowItem[] = [];

/** The parts of an infinite query a mention source reports; narrowed so any pager can be passed. */
export type WorkspaceAgentMentionInfiniteQuery = {
  fetchNextPage: () => unknown;
  hasNextPage: boolean | undefined;
  isError: boolean;
  /** `false` while the query is disabled or skipped, which is what separates "never ran" from "empty". */
  isFetched: boolean;
  isFetching: boolean;
  isLoading: boolean;
  refetch: () => unknown;
};

export type WorkspaceAgentMentionSearchQueries = {
  debouncedQuery: string;
  query: string;
};

export type UseWorkspaceAgentMentionSearchQueriesParams = {
  /** Only the source on screen searches; a closed picker has no query to run at all. */
  enabled: boolean;
  query: string;
};

/**
 * The query the user typed and the one the request is keyed on. The debounce would otherwise outlive
 * the session: closing settles it on the spot, so a picker reopened inside one interval starts on
 * its own empty query instead of spending that interval refetching the abandoned one.
 */
export const useWorkspaceAgentMentionSearchQueries = ({
  enabled,
  query,
}: UseWorkspaceAgentMentionSearchQueriesParams): WorkspaceAgentMentionSearchQueries => {
  // `useDebounce` returns `[value, cancel, callPending]` at this version; `callPending` settles a
  // pending edit early and is a no-op when there is none.
  const [debouncedQuery, , settleMentionQuery] = useDebounce(query, WORKSPACE_AGENT_MENTION_SEARCH_DEBOUNCE_MS);

  const isDebounceSettled = debouncedQuery === query;

  // A source that is not searching keeps its debounce level with the query, so it never carries a
  // stale one back: not the query a closed picker was abandoned on, nor the one an unseen source
  // was last left at.
  useEffect(() => {
    if (!enabled && !isDebounceSettled) {
      settleMentionQuery();
    }
  }, [enabled, isDebounceSettled, settleMentionQuery]);

  return useMemo(() => ({ debouncedQuery, query }), [debouncedQuery, query]);
};

export type UseWorkspaceAgentMentionSearchResultParams = {
  /** In page order — `undefined` before the first page lands. */
  items: WorkspaceAgentMentionRowItem[] | undefined;
  queries: WorkspaceAgentMentionSearchQueries;
  search: WorkspaceAgentMentionInfiniteQuery;
};

/**
 * The shared tail of every paginated mention source: dedupe the pages, drop the pager's arguments,
 * and hand the popup one stable object. `isStale` covers the gap between the input and the rows.
 */
export const useWorkspaceAgentMentionSearchResult = ({
  items,
  queries: { debouncedQuery, query },
  search,
}: UseWorkspaceAgentMentionSearchResultParams): WorkspaceAgentMentionSearchResult => {
  const { fetchNextPage, hasNextPage, isError, isFetched, isFetching, isLoading, refetch } = search;

  const dedupedItems = useMemo(() => {
    if (!items) {
      return EMPTY_ITEMS;
    }

    // An entity edited mid-scroll can repeat across cursor pages; keep the first row.
    return [...new Map(items.map((item) => [item.id, item] as const)).values()];
  }, [items]);

  const fetchNextMentionPage = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const retryMentionSearch = useCallback(() => {
    refetch();
  }, [refetch]);

  const isStale = query !== debouncedQuery || isLoading;
  const matchStrength = getWorkspaceAgentMentionMatchStrength(dedupedItems, query);

  // A fresh literal each render would re-render the popup on every keystroke.
  return useMemo(
    () => ({
      fetchNextPage: fetchNextMentionPage,
      hasNextPage: !!hasNextPage,
      // Rows this source has already answered with, for the query that is on screen.
      hasLooked: isFetched && !isStale,
      isError,
      isFetching,
      isLoading,
      isStale,
      items: dedupedItems,
      matchStrength,
      refetch: retryMentionSearch,
    }),
    [
      dedupedItems,
      fetchNextMentionPage,
      hasNextPage,
      isError,
      isFetched,
      isFetching,
      isLoading,
      isStale,
      matchStrength,
      retryMentionSearch,
    ],
  );
};
